"use client";

import React from "react";
import { cn } from "@/lib/utils";
import PrimaryButton from "@/components/ui/primary-button";
import Link from "next/link";
import SecondaryButton from "@/components/ui/secondary-button";
import { useWindowSize } from "@/hooks/use-window-size";
import { getIcon } from "@/lib/get-icon";

interface CTAPair {
  title: string;
  button: string;
  href: string;
  id?: string;
  icon?: "map-pin" | "phone" | "mail" | "clock" | "none";
}

export interface CTAServicesProps {
  id?: string;
  className?: string;
  primary?: CTAPair;
  secondary1?: CTAPair;
  secondary2?: CTAPair;
}

const CTAServices: React.FC<CTAServicesProps> = ({
  id = "cta-services",
  className,
  primary = {
    title: "Team Up With Us",
    button: "Become Our Partner",
    href: "#",
  },
  secondary1 = {
    title: "Get More Details",
    button: "Learn More",
    href: "#",
  },
  secondary2 = {
    title: "Join Our Team",
    icon: "phone",
    button: "Apply Now",
    href: "#",
  },
}) => {
  const size = useWindowSize();

  return (
    <section
      id={id}
      aria-label={id}
      className={cn("bg-secondary-500", className)}
    >
      <div className="mx-auto grid max-w-lg grid-cols-2 justify-center gap-6 bg-secondary-500 px-4 py-8 md:flex md:flex-row md:justify-between md:gap-10 md:px-6 md:py-10 lg:gap-0 lg:px-3 lg:py-[5.625rem]">
        <div className="col-span-2 flex flex-col items-center gap-2 md:col-span-1 md:items-start md:gap-4">
          <h2 className="text-nowrap font-montserrat text-subheading-3-500 text-white md:text-subheading-2 lg:text-subheading-1">
            {primary.title}
          </h2>
          <PrimaryButton
            asChild
            className="inline-block"
            color="orange-light"
            size={size === "sm" ? "sm" : "md"}
          >
            <Link
              href={primary.href}
              id={primary.id ?? "primary-cta-subpage-call-to-action"}
              prefetch
            >
              {primary.button}
            </Link>
          </PrimaryButton>
        </div>
        <div className="flex flex-col items-center gap-2 md:items-start md:gap-4">
          <h3 className="font-montserrat text-subheading-3-500 text-primary-200 md:text-subheading-2 lg:text-subheading-1">
            {secondary1.title}
          </h3>
          <SecondaryButton
            asChild
            className="inline-block"
            color="light"
            size={size === "sm" ? "sm" : "md"}
          >
            <Link
              href={secondary1.href}
              id={secondary1.id ?? "secondary-cta-subpage-cta-inquiry"}
              prefetch
            >
              {secondary1.button}
            </Link>
          </SecondaryButton>
        </div>
        <div className="flex flex-col items-center gap-2 md:items-start md:gap-4">
          <h3 className="font-montserrat text-subheading-3-500 text-primary-200 md:text-subheading-2 lg:text-subheading-1">
            {secondary2.title}
          </h3>
          <SecondaryButton
            asChild
            className="flex !h-fit flex-row items-center gap-2"
            color="light"
            size={size === "sm" ? "sm" : "md"}
          >
            <Link
              href={secondary2.href}
              id={secondary2.id ?? "secondary-cta-subpage-cta-careers"}
              prefetch
            >
              {secondary2.icon !== "none" &&
                getIcon({
                  name: secondary2.icon as
                    | "map-pin"
                    | "phone"
                    | "mail"
                    | "clock",
                  className: "w-4 h-4",
                  stroke: "#e5e4e3",
                })}
              {secondary2.button}
            </Link>
          </SecondaryButton>
        </div>
      </div>
    </section>
  );
};

export default CTAServices;
